import type {SelectField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {useLinkResolver} from 'prismicio'
import type {
  PricingSectionSliceLargePrimarySecondPackageItem,
  PricingSectionSliceLargePrimaryThirdPackageItem,
} from 'prismicio-types'
import * as icons from 'slices/PricingSection/components/Icons'
import {ButtonV2} from 'src/common/ButtonV2'
import {Icon as IconComponent} from 'src/common/Icon'
import {InfoTooltip} from 'src/common/InfoTooltip'

export const LargeModuleCard = ({
  backgroundColor,
  sectionLength,
  item,
  index,
}: {
  backgroundColor: SelectField<'Green/700' | 'Moss White'>
  sectionLength: number
  item: PricingSectionSliceLargePrimarySecondPackageItem | PricingSectionSliceLargePrimaryThirdPackageItem
  index: number
}) => {
  const {linkResolver} = useLinkResolver()
  const hasLink = isFilled.link(item.link)
  const Icon = icons[item.module_icon as keyof typeof icons]
  // 4+ modules use a compact card layout with smaller sizing
  const isCompactCard = sectionLength >= 4

  return (
    <div
      className={cn(
        'relative flex flex-col justify-between gap-6 rounded-lg bg-green-dark p-6 text-moss-white lg:min-h-[280px]',
        {
          'bg-green-dark': backgroundColor === 'Green/700',
          'bg-moss-white': backgroundColor === 'Moss White',
          'w-full md:max-lg:w-[calc(50%_-_8px)]': isCompactCard,
          'w-full': !isCompactCard,
          'lg:!min-h-[260px]': isCompactCard,
        },
      )}
      key={`module-card-second-row-${index}`}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Icon color={item.background_color === 'Green/700' ? 'white' : '#717171'} />
          <h5
            className={cn('text-18 font-medium leading-[30px]', {
              'text-moss-black': item.background_color === 'Moss White',
              'text-moss-white': item.background_color === 'Green/700',
            })}
          >
            {item.module_title}
          </h5>
          {isFilled.richText(item.tooltip) && (
            <InfoTooltip
              id={`${index}-${item.module_title?.replace(/ /g, '_')}`}
              data-page-location={`component-${index}`}
              data-type={`selector-${item.module_title}`}
              data-mktg-id="tool-tips-icon"
              data-page-name="pricing"
              className="mt-1.5 self-start"
            >
              <PrismicRichText
                field={item.tooltip}
                components={{
                  paragraph: ({children: text}) => <p className="text-body-xxs pb-2 uppercase text-grey-150">{text}</p>,
                  list: ({children: listItems}) => <ul className="flex flex-col gap-2 pb-2">{listItems}</ul>,
                  listItem: ({children: text}) => (
                    <li className="text-body-bold-s flex items-center gap-2 text-moss-white">
                      <div className="relative flex size-3 min-w-3 justify-center rounded-full bg-green-300">
                        <IconComponent name="check-mark" className="size-1.5 self-center text-moss-white" />
                      </div>
                      {text}
                    </li>
                  ),
                  oList: ({children: listItems}) => <ol className="flex flex-col gap-2 ">{listItems}</ol>,
                  oListItem: ({children: text}) => (
                    <li className="text-body-bold-s flex items-center gap-2 text-moss-white">
                      <div className="relative flex size-3 justify-center rounded-full bg-ui-red-450">
                        <IconComponent name="cross" className="size-1.5 self-center text-moss-white" />
                      </div>
                      {text}
                    </li>
                  ),
                }}
              />
            </InfoTooltip>
          )}
        </div>
        <PrismicRichText
          field={item.description}
          components={{
            list: ({children}) => (
              <ul
                className={cn('text-tg-body-m flex list-none flex-col gap-2', {
                  'text-moss-white': item.background_color === 'Green/700',
                  'text-moss-grey-550': item.background_color === 'Moss White',
                })}
              >
                {children}
              </ul>
            ),
            listItem: ({children}) => (
              <li className="flex gap-3">
                <div className="mt-[7px] h-[5px] w-[5px] rounded-[1px] bg-moss-white/[70%]" />
                {children}
              </li>
            ),
            paragraph: ({children}) => (
              <p
                className={cn('text-tg-body-m', {
                  'text-moss-white': item.background_color === 'Green/700',
                  'text-moss-grey-550': item.background_color === 'Moss White',
                })}
              >
                {children}
              </p>
            ),
          }}
        />
      </div>
      {hasLink && (
        <ButtonV2
          variant={backgroundColor === 'Green/700' ? 'icon-dark' : 'secondary'}
          href={linkResolver(item.link)}
          id={`pricing-module-card-${index}`}
          className="h-[38px] w-fit !pl-[14px] !pr-[7px]"
          trackingData={{
            mktgId: 'pricing-section-module-card',
            location: `pricing-module-card-${index}`,
            dataType: 'button',
            pageName: 'pricing',
          }}
        >
          <div className="flex items-center gap-[14px]">
            <span className="whitespace-nowrap">{item.link.text || 'Learn more'}</span>
            <div className="flex shrink-0 items-center justify-center rounded-[2px] bg-green-neon p-2">
              <IconComponent name="arrow" className="size-2 text-green-dark" />
            </div>
          </div>
        </ButtonV2>
      )}
      {item.background_color === 'Moss White' && (
        <div className="pointer-events-none absolute inset-0 rounded-lg border border-[rgba(19,18,18,.1)]" />
      )}
    </div>
  )
}
